import { useEffect, useId, useRef, useState } from "react";
import { Wrench, Heart, Film, BookOpen, Clapperboard, Briefcase, Check } from "lucide-react";
import { useI18n } from "../i18n";
import type { TranslationKey } from "../i18n";
import { useLayers } from "../hooks/useLayers";
import { FALLBACK_COMPANION, type CompanionDescriptor } from "../brain/persona";

// Session vocabulary. "creative" and "build" are LEGACY session_type values from the DB.
// "storyboard-studio" and "film-editing" are legacy ids surfaced via useLayers.
// The dialog speaks layer ids directly — it does NOT use SESSION_TYPE_TO_LAYER_ID.
// SessionType is kept for the AppShell prop so TypeScript catches unknown strings.
export type SessionType =
  | 'personal'
  | 'writing'
  | 'office'
  | 'film-studio'
  | 'building'
  | 'storyboard-studio'   // legacy — existing sessions only
  | 'film-editing'         // legacy — existing sessions only
  | 'creative'             // legacy
  | 'build';               // legacy

// Room layer ids — mirrors LayerId from the gateway registry but defined
// here so the client tsconfig doesn't need to reach into src/gateway.
export type LayerIdClient =
  | 'personal'
  | 'writing'
  | 'office'
  | 'film-studio'
  | 'building';

export interface NewSessionOpts {
  type: SessionType;
  label?: string;
  companion?: string;
  /** Create the chat inside this project (server validates). */
  projectId?: string;
}

interface NewSessionDialogProps {
  open: boolean;
  onSelect: (opts: NewSessionOpts) => void;
  onClose: () => void;
  /** PATH B SLICE 4 — server companion allowlist (from the init frame).
   *  null/undefined/empty falls back to FALLBACK_COMPANION (persona.ts) —
   *  the single-companion OSS default, rendered as today's static pill. */
  companions?: CompanionDescriptor[] | null;
}

// Icon mapping keyed by layer id.
const LAYER_ICONS: Record<string, typeof Heart> = {
  'personal': Heart,
  'writing': BookOpen,
  'office': Briefcase,
  'storyboard-studio': Film,
  'film-editing': Clapperboard,
  'film-studio': Film,
  'building': Wrench,
};

// Explicit grid reading order — Personal · Writer's Room · Office · Film
// Studio · Building. The server's /api/layers response order (registry
// insertion order) doesn't match this reading order, so the dialog always
// re-sorts client-side rather than trusting wire order. Layer ids absent
// from this list (legacy storyboard-studio/film-editing, both `hidden: true`
// server-side and already filtered out of /api/layers) sort after every
// named id, so a future addition doesn't silently vanish.
const CARD_DISPLAY_ORDER = ['personal', 'writing', 'office', 'film-studio', 'building'];

// i18n label keys keyed by layer id.
// Keys are typed as TranslationKey so the type checker covers missing values.
const LAYER_LABEL_KEYS: Record<string, TranslationKey> = {
  'personal': 'session.personal.new',
  'writing': 'session.writing',
  'office': 'session.office',
  'storyboard-studio': 'session.storyboard-studio',
  'film-editing': 'session.film-editing',
  'building': 'session.building',
  'film-studio': 'session.film-studio',
};

const LAYER_DESC_KEYS: Record<string, TranslationKey> = {
  'personal': 'session.personal.new.desc',
  'writing': 'session.writing.desc',
  'office': 'session.office.desc',
  'storyboard-studio': 'session.storyboard-studio.desc',
  'film-editing': 'session.film-editing.desc',
  'building': 'session.building.desc',
  'film-studio': 'session.film-studio.desc',
};

export function NewSessionDialog({ open, onSelect, onClose, companions }: NewSessionDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const titleId = useId();
  const { t } = useI18n();
  const { layers } = useLayers();
  const [selectedType, setSelectedType] = useState<string>("personal");
  const [label, setLabel] = useState("");

  // PATH B SLICE 4: the picker list. Falls back to a single-item list built
  // from persona.ts's build-time constants when the server hasn't sent a
  // companion allowlist yet (or configures exactly one companion) — this
  // degenerates to today's static pill with zero picker chrome.
  const companionList = companions && companions.length > 0 ? companions : [FALLBACK_COMPANION];
  const [selectedCompanionId, setSelectedCompanionId] = useState<string>(companionList[0].id);

  useEffect(() => {
    if (!open) return;
    setSelectedType("personal");
    setLabel("");
    setSelectedCompanionId(companionList[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus management: capture trigger, focus dialog card on open; restore on close.
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    cardRef.current?.focus();
    return () => {
      (triggerRef.current as HTMLElement | null)?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Focus trap: cycle Tab/Shift+Tab through all focusable elements in the dialog.
      if (e.key === "Tab" && cardRef.current) {
        const focusable = Array.from(
          cardRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled])'
          )
        );
        if (focusable.length === 0) return;
        e.preventDefault();
        const activeIndex = focusable.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey) {
          const prev = activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1;
          focusable[prev].focus();
        } else {
          const next = activeIndex >= focusable.length - 1 ? 0 : activeIndex + 1;
          focusable[next].focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Render in the fixed reading order (CARD_DISPLAY_ORDER), not whatever
  // order /api/layers happened to return — see that constant's comment.
  // -1 (not found) is normalized to +Infinity so an unlisted id sorts last,
  // never first (a bare indexOf diff would put it first).
  const rank = (id: string) => {
    const i = CARD_DISPLAY_ORDER.indexOf(id);
    return i === -1 ? Infinity : i;
  };
  // Building is a stub with no module behind it yet (available:false) — a
  // visible-but-disabled "Coming Soon" card for it would be the 5th card
  // orphaned alone on the grid's last row, the exact bug this dialog was
  // already fixed for once. Hide any layer that isn't available yet rather
  // than rendering it disabled; every real entitled-but-not-yet-granted
  // layer (available:true, entitled:false) still renders, disabled.
  const orderedLayers = layers
    .filter((layer) => layer.available)
    .sort((a, b) => rank(a.id) - rank(b.id));

  // Fischer MEDIUM (B3b fix round): Office + a non-rook companion 403s
  // server-side and useSession.ts's createSession silently swallows it (no
  // toast system exists). Prevent the combination client-side instead: the
  // Office card disables whenever the selected companion isn't rook (below),
  // and switching companion AWAY from rook while Office is selected
  // deselects it back to Personal rather than leaving an unsubmittable card
  // selected with no feedback.
  function handleCompanionSelect(companionId: string) {
    setSelectedCompanionId(companionId);
    if (companionId !== 'rook' && selectedType === 'office') {
      setSelectedType('personal');
    }
  }

  function handleStart() {
    onSelect({
      type: selectedType as SessionType,
      label: label.trim() || undefined,
      // PATH B SLICE 4: companion is the lowercase identity id (e.g. 'rook' |
      // 'rainer') the gateway's allowlist validates against — NOT a display
      // name. Sending a display name here would be rejected by the server's
      // resolveCompanion() as an unknown identity.
      companion: selectedCompanionId,
    });
  }

  return (
    <div
      className="dialog-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div
        className="dialog-card"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="dialog-title display" id={titleId}>{t("sessionDialog.title")}</div>
        <div className="dialog-subtitle">{t("sessionDialog.subtitle")}</div>

        <div className="session-type-grid">
          {orderedLayers.map((layer) => {
            const Icon = LAYER_ICONS[layer.id] ?? Heart;
            const labelKey = LAYER_LABEL_KEYS[layer.id];
            const descKey = LAYER_DESC_KEYS[layer.id];
            const isSelected = selectedType === layer.id;
            // B3b fix round (Fischer MEDIUM): Office is rook-only server-side
            // — disable the card for any other selected companion rather
            // than letting the user submit a combination that always 403s.
            const isOfficeLockedForCompanion = layer.id === 'office' && selectedCompanionId !== 'rook';
            return (
              <button
                key={layer.id}
                className={`session-type-card session-type-${layer.id}${isSelected ? " selected" : ""}`}
                aria-pressed={isSelected ? 'true' : 'false'}
                disabled={!layer.available || !layer.entitled || isOfficeLockedForCompanion}
                onClick={() => setSelectedType(layer.id)}
              >
                <Icon size={24} />
                <div className="session-type-label">{labelKey ? t(labelKey) : layer.label}</div>
                {isOfficeLockedForCompanion ? (
                  <div className="session-type-desc">{t("session.office.rookOnly")}</div>
                ) : descKey && (
                  <div className="session-type-desc">{t(descKey)}</div>
                )}
              </button>
            );
          })}
        </div>

        <div className="dialog-companion-section">
          <div className="label">{t("sessionDialog.companion")}</div>
          {companionList.length <= 1 ? (
            // N=1 — exactly today's static pill, zero picker chrome.
            <div className="companion-pill">{companionList[0].displayName}</div>
          ) : (
            <div className="companion-picker" role="group" aria-label={t("sessionDialog.companion")}>
              {companionList.map((c) => {
                const isSelected = selectedCompanionId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`companion-pill companion-pill-option${isSelected ? " selected" : ""}`}
                    aria-pressed={isSelected ? 'true' : 'false'}
                    title={c.subtitle}
                    onClick={() => handleCompanionSelect(c.id)}
                  >
                    {/* WCAG 1.4.1 — selection must not rely on color alone.
                        aria-hidden: aria-pressed already announces state to AT. */}
                    {isSelected && <Check size={12} aria-hidden="true" />}
                    {c.displayName}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="dialog-label-section">
          <label className="label" htmlFor="session-label-input">{t("sessionDialog.labelHint")}</label>
          <input
            id="session-label-input"
            className="dialog-label-input"
            type="text"
            placeholder={t("sessionDialog.labelPlaceholder")}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleStart(); }}
            maxLength={100}
          />
        </div>

        <div className="dialog-actions">
          <button className="btn btn-primary" onClick={handleStart}>
            {t("sessionDialog.startSession")}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            {t("sessionDialog.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
